import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AXLE, Tabs, today } from "@/components/tyres/common";
import { api, errMsg } from "@/lib/api";

const NONE = "__none";
const F = ({ label, children, span }) => <div className={`min-w-0 space-y-1.5 ${span ? "sm:col-span-2" : ""}`}><Label>{label}</Label>{children}</div>;
const fits = (t, model) => !t.vehicle_models?.length || t.vehicle_models.includes((model || "").toUpperCase());

const TypeSelect = ({ value, onChange, options, stock, testId, none }) => (
  <Select value={value} onValueChange={onChange}>
    <SelectTrigger className="min-w-0 [&>span]:truncate" data-testid={testId}><SelectValue placeholder="Seleziona gomma" /></SelectTrigger>
    <SelectContent>
      {none && <SelectItem value={NONE}>Non note / non registrate</SelectItem>}
      {options.map((t) => <SelectItem key={t.id} value={t.id}>{t.label}{stock ? ` — a magazzino: ${stock[t.id]?.NUOVA ?? 0} nuove, ${stock[t.id]?.USATA ?? 0} usate` : ""}</SelectItem>)}
    </SelectContent>
  </Select>
);

export const ChangeDialog = ({ vehicle, sid, isAdmin, types, shops, onClose, onSaved }) => {
  const cur = vehicle.current;
  const [f, setF] = useState({ date: today(), km: "", axle: "TUTTE", new_type_id: "", new_condition: "NUOVA", old_type_id: cur.front?.type_id || NONE, old_rear_type_id: cur.rear?.type_id || NONE,
    removed_to: "SMALTITE", shop_id: shops.defaults[sid] || NONE, notes: "" });
  const [all, setAll] = useState(false);
  const [stock, setStock] = useState({});
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.get("/tyres/stock", { params: { station_id: isAdmin ? sid : undefined } }).then((r) => setStock(Object.fromEntries(r.data.items.map((i) => [i.type_id, i]))));
  }, [sid, isAdmin]);
  const set = (k) => (v) => setF((p) => ({ ...p, [k]: v?.target ? v.target.value : v }));
  const setAxle = (a) => setF((p) => ({ ...p, axle: a, old_type_id: (a === "POSTERIORI" ? cur.rear : cur.front)?.type_id || NONE }));
  const options = useMemo(() => types.filter((t) => all || fits(t, vehicle.model)), [types, all, vehicle.model]);
  const olds = [f.old_type_id, ...(f.axle === "TUTTE" ? [f.old_rear_type_id] : [])].map((id) => types.find((t) => t.id === id)).filter(Boolean);
  const canStore = olds.some((t) => t.season !== "QUATTRO_STAGIONI");
  const qty = f.axle === "TUTTE" ? 4 : 2;

  const save = async (force = false) => {
    setBusy(true);
    try {
      await api.post("/tyres/changes", { ...f, km: f.km === "" ? null : Number(f.km), old_type_id: f.old_type_id === NONE ? null : f.old_type_id,
        old_rear_type_id: f.axle !== "TUTTE" || f.old_rear_type_id === NONE ? null : f.old_rear_type_id,
        shop_id: f.shop_id === NONE ? null : f.shop_id, removed_to: canStore ? f.removed_to : "SMALTITE", vehicle_id: vehicle.id, force });
      toast.success("Cambio gomme registrato");
      onSaved();
    } catch (e) {
      if (e.response?.status === 409 && window.confirm(`${errMsg(e)}.\n\nRegistrare comunque il cambio? Il magazzino andrà in negativo.`)) return save(true);
      toast.error(errMsg(e));
    } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[92vh] overflow-y-auto sm:max-w-2xl" data-testid="tyre-change-dialog">
        <DialogHeader>
          <DialogTitle>Cambio gomme · {vehicle.plate}</DialogTitle>
          <DialogDescription>{vehicle.model || "Modello non indicato"}. Le gomme montate ({qty}) vengono scalate dal magazzino della stazione.</DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 sm:grid-cols-2">
          <F label="Gomme da cambiare" span><Tabs value={f.axle} onChange={setAxle} items={Object.entries(AXLE)} prefix="tyre-axle" /></F>
          <F label="Data cambio"><Input type="date" value={f.date} max={today()} onChange={set("date")} data-testid="tyre-change-date" /></F>
          <F label="Km del furgone (servono per i km fatti)"><Input type="number" min="0" value={f.km} onChange={set("km")} data-testid="tyre-change-km" /></F>
          <F label={f.axle === "TUTTE" ? "Gomme smontate anteriori (precedenti)" : "Gomme smontate (precedenti)"} span>
            <TypeSelect value={f.old_type_id} onChange={set("old_type_id")} options={types} none testId="tyre-old-type-select" />
          </F>
          {f.axle === "TUTTE" && <F label="Gomme smontate posteriori (precedenti)" span>
            <TypeSelect value={f.old_rear_type_id} onChange={set("old_rear_type_id")} options={types} none testId="tyre-old-rear-type-select" />
          </F>}
          {canStore && (
            <F label="Gomme smontate: dove vanno?" span>
              <Tabs value={f.removed_to} onChange={set("removed_to")} items={[["MAGAZZINO", "Tornano a magazzino"], ["SMALTITE", "Smaltite"]]} prefix="tyre-removed" />
            </F>
          )}
          {olds.some((t) => t.season === "QUATTRO_STAGIONI") && <p className="text-xs text-slate-500 sm:col-span-2">Le gomme 4 stagioni smontate vengono sempre smaltite.</p>}
          <F label="Gomme montate (attuali)" span>
            <TypeSelect value={f.new_type_id || undefined} onChange={set("new_type_id")} options={options} stock={stock} testId="tyre-new-type-select" />
            <label className="flex items-center gap-2 text-xs text-slate-500"><input type="checkbox" checked={all} onChange={(e) => setAll(e.target.checked)} data-testid="tyre-show-all-types" /> Mostra anche le gomme non assegnate al modello {vehicle.model}</label>
          </F>
          <F label="Gomme montate: stato"><Tabs value={f.new_condition} onChange={set("new_condition")} items={[["NUOVA", "Nuove"], ["USATA", "Usate"]]} prefix="tyre-cond" /></F>
          <F label="Gommista">
            <Select value={f.shop_id} onValueChange={set("shop_id")}>
              <SelectTrigger className="min-w-0" data-testid="tyre-change-shop"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value={NONE}>Nessuno</SelectItem>{shops.shops.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
            </Select>
          </F>
          <F label="Note" span><Textarea value={f.notes} onChange={set("notes")} rows={2} data-testid="tyre-change-notes" /></F>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Annulla</Button>
          <Button onClick={() => save()} disabled={busy || !f.new_type_id || !f.date} data-testid="tyre-change-save">{busy ? "Salvataggio..." : "Registra cambio"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
